import type { PropsWithChildren } from "react"
import React, { createContext, useEffect, useState } from "react"
import { loadStripe } from "@stripe/stripe-js"
import { Elements } from "@stripe/react-stripe-js"
import type { ApolloError } from "@apollo/client"

import LoadingOrErrorContainer from "~components/shared/todo-lib-react-components/loading-or-error-container"
import { useStripeOptions } from "~stripe/stripe-utils"
import type { StripeSetupIntentFieldsFragment } from "~graphql/generated/graphql"

interface ISetupIntentContext {
    setupIntent: StripeSetupIntentFieldsFragment | null
}
interface CommonSetupIntentHookArgs {
    onCompleted: (setupIntent: StripeSetupIntentFieldsFragment) => void
}
interface SetupIntentHookReturnType {
    createSetupIntent: () => void
    isLoading: boolean
    error: Error | ApolloError | null | undefined
}

const SetupIntentContext = createContext<ISetupIntentContext>({
    setupIntent: null,
})

interface Props<UseSetupIntentArgs> extends PropsWithChildren {
    readonly useSetupIntentHook: (
        args: UseSetupIntentArgs & CommonSetupIntentHookArgs
    ) => SetupIntentHookReturnType
    readonly setupIntentHookArgs: UseSetupIntentArgs
    readonly noPadding?: boolean
}

export function SetupIntentProvider<UseSetupIntentArgs>({
    useSetupIntentHook,
    setupIntentHookArgs,
    noPadding,
    children,
}: Props<UseSetupIntentArgs>) {
    const [setupIntent, setSetupIntent] =
        useState<StripeSetupIntentFieldsFragment | null>(null)

    const stripePromise = loadStripe(process.env.GATSBY_STRIPE_PUBLIC_KEY ?? "")
    const { createSetupIntent, isLoading, error } = useSetupIntentHook({
        ...setupIntentHookArgs,
        onCompleted: setSetupIntent,
    })

    const options = useStripeOptions(setupIntent)
    const stringifiedSetupIntentArgs = JSON.stringify(setupIntentHookArgs)

    // Create the setup intent when args change. The setup intent
    // hooks will handle skipping if they don't have all the args they need.
    useEffect(() => {
        createSetupIntent()
    }, [stringifiedSetupIntentArgs])

    return (
        <LoadingOrErrorContainer
            isLoading={isLoading}
            error={error}
            shouldRenderChildren={Boolean(setupIntent)}
            noPadding={noPadding}
        >
            <SetupIntentContext.Provider value={{ setupIntent: setupIntent! }}>
                <Elements stripe={stripePromise} options={options}>
                    {children}
                </Elements>
            </SetupIntentContext.Provider>
        </LoadingOrErrorContainer>
    )
}
